import EventEmitter from "base/EventEmitter";
import GlobalEvents from "base/GlobalEvents";
import ValueHelper from "base/ValueHelper";
import debounce from "debounce";

export default class AutoComplete extends EventEmitter {
    constructor($input, options = {}) {
        super();
        this.options = $.extend(true, {}, AutoComplete.DEFAULT_OPTIONS, options);

        this.$input = $input;
        this.items = [];
        this.uniqueId = ValueHelper.random_string();

        if (this.options.updateDebounce === null) {
            this.debouncedUpdate = this.updateItems.bind(this);
        } else {
            this.debouncedUpdate = debounce(this.updateItems.bind(this), this.options.updateDebounce);
        }

        this.buildElement();
        this.bindEvents();

        this.setItems(this.items);

        GlobalEvents.emit(EVENT_GLOBAL_SELECT_INITIALISED, this);

        AutoComplete.instances.push(this);
    }

    static close_all() {
        AutoComplete.instances.forEach((autocomplete) => autocomplete.close(true));
    }

    _close() {
        this.emit("close");
        this.doClose();
        this.$input.attr("aria-expanded", "false").attr("aria-activedescendant", "");
    }

    bindEvents() {
        const me = this;

        this.$input.on("focus", () => {
            this.debouncedUpdate();
        }).on("blur", () => {
            this.close();
        });
        this.$list.on("focus", "button,a", () => {
            this.open();
        }).on("blur", "button,a", () => {
            this.close();
        });

        this.$listWrap.on("click", "button,a", function () {
            if (me.options.followLinks && $(this).is("a")) {
                window.location.href = $(this).attr("href");
                return false;
            }

            me.setValue($(this).parents("li").data("value"));
            me.close(true);
            return false;
        });

        this.$input.on("input", (e) => {
            this.debouncedUpdate();
        }).on("keydown", (e) => {
            let focusClass = this.options.classes.listBtnFocused;
            if (e.which === 38 /*UP*/ || e.which === 40 /*DOWN*/) {
                if (!this.isOpen()) {
                    this.open();
                    return false;
                }
                let direction = e.which === 38 ? -1 /*Up*/ : 1 /*Down*/;
                let current = this.$list.find("." + focusClass).parent().index();

                let targetIndex;
                // If none are already selected
                if (current === -1) {
                    if (direction === 1) {
                        // Select the first element
                        targetIndex = 0;
                    } else {
                        // Select the last element
                        targetIndex = -1;
                    }
                } else {
                    targetIndex = current + direction;
                }

                if (targetIndex < 0) {
                    // Select the last element
                    targetIndex = this.$list.children().length - 1;
                } else if (targetIndex >= this.items.length) {
                    // Select the first element
                    targetIndex = 0;
                }
                let $target = this.$list.children().eq(targetIndex);

                this.$list.find("." + focusClass).removeClass(focusClass);
                $target.find("." + this.options.classes.listBtn).addClass(focusClass);

                this.$input.attr("aria-activedescendant", "autocomplete-" + this.uniqueId + "-item-" + targetIndex);

                if ($target.length > 0) {
                    let position = $target.position().top;
                    let scrollTop = this.$scrollWrap.scrollTop();
                    let scrollBottom = scrollTop + this.$scrollWrap.innerHeight();

                    let top = position + scrollTop;
                    let bottom = top + $target.outerHeight();

                    if (top < scrollTop) {
                        this.$scrollWrap.scrollTop(top);
                    } else if (bottom > scrollBottom) {
                        this.$scrollWrap.scrollTop(bottom - this.$scrollWrap.innerHeight());
                    }
                }

                return false;
            } else if (e.which === 27 /*Esc*/) {
                this.close(true);

                return false;
            } else if (e.which === 13 /*Enter*/) {
                if (this.isOpen() && this.$list.find("." + focusClass).length > 0) {
                    this.$list.find("." + focusClass).click();
                    return false;
                }
            }
        });
    }

    buildElement() {
        let listId = "autocomplete-" + this.uniqueId;
        this.$list = (eotwJSXFactory('ul', {class: this.options.classes.list}));
        this.$listWrap = (
            eotwJSXFactory('div', {class: this.options.classes.listWrap + " " + this.options.modifiers.map((modifier) => this.options.classes.listWrap + "--" + modifier).join(" "), role: "listbox", id: listId}, [
                eotwJSXFactory('div', {class: this.options.classes.scrollWrap}, [
                    this.$list
                ])
            ])
        );

        this.$scrollWrap = this.$listWrap.find("." + this.options.classes.scrollWrap);

        this.$input.after(this.$listWrap);

        this.$input.attr({
            "autocomplete": "off",
            "spellcheck": "false",
            "aria-autocomplete": "list",
            "role": "combobox",
            "aria-expanded": "false",
            "aria-controls": listId,
            "aria-owns": listId,
            "aria-haspopup": "true",
        });

        if (!this.$input.is("[placeholder]")) {
            this.$input.attr("placeholder", "");
        }
    }

    buildList(thenOpen = false) {
        if (this.items === null || this.items.length === 0) {
            if (this.options.showEmpty) {
                this.$list.html(this.options.buildEmptyItem.call(this));
            } else {
                this.$list.html("");
                this.close(true);
                return;
            }
        } else {
            this.$list.html(this.items.map((item, index) => {
                let $item = this.options.buildItem.call(this, item, index);
                $item.data("value", item);
                return $item;
            }));
        }
        if (thenOpen) {
            this.open();
        }
    }

    close(immediate = false) {
        if (!this.isOpen()) {
            return;
        }
        if (this.closeTimeout) {
            clearTimeout(this.closeTimeout);
        }
        if (immediate) {
            this._close();
        } else {
            this.closeTimeout = setTimeout(() => this._close(), 150);
        }
    }

    doClose() {
        this.$listWrap.removeClass(this.options.classes.listShown);
    }

    doOpen() {
        this.$listWrap.addClass(this.options.classes.listShown);
    }

    isOpen() {
        return this.$listWrap.is("." + this.options.classes.listShown);
    }

    open() {
        if (this.closeTimeout) {
            clearTimeout(this.closeTimeout);
        }

        if (this.isOpen() || this.items === null || !this.emit("open")) {
            return;
        }

        AutoComplete.close_all();

        this.$input.attr("aria-expanded", "true");
        this.doOpen();
    }

    setItems(items, thenOpen = false) {
        this.items = items;

        this.buildList(thenOpen);
    }

    setValue(value) {
        if (value === null) {
            this.$input.val("");
        } else {
            this.$input.val(this.options.getTextValue(value));
        }
        this.emit("selected", value);

        this.value = value;
        this.close(true);
        this.$input.trigger("validate-soft");
    }

    toggle() {
        if (this.isOpen()) {
            this.close(true);
        } else {
            this.open();
        }
    }

    async updateItems(thenOpen = true, force = false) {
        let value = this.$input.val();

        if (!force && value === this.lastValue) {
            this.buildList(thenOpen);
            return;
        }

        this.lastValue = value;
        if (value === "" && !this.options.showAll) {
            this.setItems(null, thenOpen);
            return;
        }

        if (this.options.getResults instanceof Function) {
            let items = await this.options.getResults.call(this, value);
            if (this.lastValue !== value) {
                return;
            }

            this.emit("resultsUpdated", items);
            this.setItems(items, thenOpen);
        } else {
            console.error("Invalid getResults function", this.options);
        }
    }
}

/** @var {Array<AutoComplete>} AutoComplete.instances */
AutoComplete.instances = [];

AutoComplete.DEFAULT_OPTIONS = {
    classes: {
        listWrap: "autocomplete",
        listShown: "autocomplete--shown",
        list: "autocomplete__list",
        scrollWrap: "autocomplete__scroll-wrap",
        listItem: "autocomplete__item",
        listItemNone: "autocomplete__item--none",
        listBtn: "autocomplete__btn",
        listBtnFocused: "autocomplete__btn--focus",
    },
    modifiers: [],
    data: [],
    showEmpty: false,
    showAll: false,
    updateDebounce: null,
    followLinks: true,
    getResults: function (val) {
        if (val === "") {
            return this.options.data;
        }
        val = val.toLowerCase();
        return this.options.data.filter((item) => this.options.getTextValue(item).toLowerCase().indexOf(val) !== -1);
    },
    buildItem: function (item, index) {
        return (
            eotwJSXFactory('li', {class: this.options.classes.listItem}, [
                eotwJSXFactory('button', {tabIndex: "-1", id: "autocomplete-" + this.uniqueId + "-item-" + index, role: "option", type: "button", class: this.options.classes.listBtn}, [this.options.getTextValue(item)])
            ])
        );
    },
    getTextValue: function (item) {
        return item;
    },
    buildEmptyItem: function () {
        return (
            eotwJSXFactory('li', {class: this.options.classes.listItem + " " + this.options.classes.listItemNone}, [
                eotwJSXFactory('span', null, ["No results"])
            ])
        );
    },
};

export const EVENT_GLOBAL_SELECT_INITIALISED = 'base.autocomplete.initalised';
